import {
  Autocomplete,
  Button,
  ButtonGroup,
  DialogActions,
  Grid,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { getDocumentoVariables } from "../../../../../containers/smartForms/components/helper";
import { language } from "../../../../../language";
import { getUniqueDocumentServiceFields } from "../../../../../util/camundaPropertiesPanelUtils";
import { customFetch } from "../../../../../util/customFetch";
import GenericModal from "../../../../modal-closebtn";
import GenericSnackbar from "../../../../snackbar/generic-snackbar";

const FormPadding = styled("div")(() => ({
  padding: "1% 1%",
}));

function OverrideModalActions({ handleSubmit, handleClose }) {
  return (
    <DialogActions>
      <ButtonGroup>
        <Button variant="outlined" size="large" color="primary" onClick={handleClose}>
          {language.cancel}
        </Button>
        <Button variant="contained" size="large" color="primary" onClick={handleSubmit}>
          {language.save}
        </Button>
      </ButtonGroup>
    </DialogActions>
  );
}

OverrideModalActions.propTypes = {
  handleSubmit: PropTypes.func,
  handleClose: PropTypes.func,
};

const DEFAULT_CONFIG = {
  idUnidade: "",
  documentos: [],
  tipo: "R",
  idProcedimento: "",
  protocoloProcedimento: "",
  idSerie: "",
  numero: "",
  data: "",
  descricao: "",
  idTipoConferencia: "3",
  remetenteSigla: "",
  remetenteNome: "",
  interessadosSigla: "",
  interessadosNome: "",
  destinatariosSigla: "",
  destinatariosNome: "",
  observacao: "nenhuma",
  nivelAcesso: "1",
  idHipoteseLegal: "33",
  idArquivo: "",
  campoNome: "",
  campoValor: "",
  sinBloqueado: "N",
  resultVariable: "sei_incluir_sincrono_result",
};

function ModelerModalIntegrarSei({
  saveIntegrarSeiConfig,
  businessObject,
  element,
  extensionElements,
  bpmnFactory,
  FormComponents,
  inputValues,
}) {
  const [open, setOpen] = useState(true);
  const [jsonConfig, setJsonConfig] = useState(DEFAULT_CONFIG);
  const [hasConfigError, setHasConfigError] = useState(false);
  const [seiIntegration, setSeiIntegration] = useState({
    units: [],
    series: [],
  });

  let documentVariables = getDocumentoVariables(FormComponents) || [];
  documentVariables = documentVariables.concat(getUniqueDocumentServiceFields(businessObject));
  documentVariables = documentVariables
    .filter((value, index, self) => index === self.findIndex((obj) => obj.id === value.id))
    .map((value) => value.id);

  useEffect(() => {
    if (inputValues) {
      setJsonConfig({
        ...DEFAULT_CONFIG,
        ...inputValues,
        identificacaoServico: undefined,
        documentos: inputValues?.documentos || [],
      });
    }
  }, [inputValues]);

  useEffect(() => {
    let active = true;
    const tenant = sessionStorage.getItem("tenant");

    if (!tenant) {
      return () => {
        active = false;
      };
    }

    customFetch(`/api/smartbpm/v1/{tenant}/organizations/${tenant}/sei-integration`)
      .then((data) => {
        if (!active) {
          return;
        }
        setSeiIntegration({
          units: data?.units || [],
          series: data?.series || [],
        });
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setSeiIntegration({ units: [], series: [] });
      });

    return () => {
      active = false;
    };
  }, []);

  const handleClose = () => setOpen(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setJsonConfig((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeDocumentos = (event, newValue) => {
    setJsonConfig((prev) => ({
      ...prev,
      documentos: newValue,
    }));
  };

  const handleChangeSelect = (name) => (event, newValue) => {
    setJsonConfig((prev) => ({
      ...prev,
      [name]: newValue?.id || "",
    }));
  };

  const validateConfig = () => {
    const errors = [];
    if (!jsonConfig.idUnidade) {
      errors.push("ID da unidade é obrigatório.");
    }
    if (!jsonConfig.documentos || jsonConfig.documentos.length <= 0) {
      errors.push(language.selectAtLeastOneDocumentToSend || "Selecione ao menos um documento para envio.");
    }
    return errors;
  };

  const handleSave = () => {
    const errors = validateConfig();
    if (errors.length > 0) {
      setHasConfigError(errors);
      return;
    }

    const { identificacaoServico, ...configToSave } = jsonConfig;

    saveIntegrarSeiConfig(
      businessObject,
      element,
      extensionElements,
      bpmnFactory,
      configToSave,
    );
    handleClose();
  };

  const renderField = (name, label, props = {}) => (
    <Grid item size={{ xs: 12, md: props.md || 4 }}>
      <TextField
        fullWidth
        variant="outlined"
        id={name}
        name={name}
        label={label}
        value={jsonConfig[name] || ""}
        onChange={handleChange}
        {...props}
      />
    </Grid>
  );

  const renderIntegrationSelect = (name, label, options, props = {}) => {
    const selected = options.find((option) => option.id === jsonConfig[name]) || null;

    return (
      <Grid item size={{ xs: 12, md: props.md || 4 }}>
        <Autocomplete
          value={selected}
          onChange={handleChangeSelect(name)}
          options={options}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              variant="outlined"
              id={name}
              name={name}
              label={label}
              required={props.required}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    open && (
      <GenericModal
        title="Integra SEI"
        handleClose={handleClose}
        size="lg"
        bigHeight
        open={true}
        handleSubmit={handleSave}
        btnActionTitle={language.save}
        btnCancelTitle={language.cancel}
        overrideDialogActions={() =>
          OverrideModalActions({ handleSubmit: handleSave, handleClose })
        }
      >
        <FormPadding>
          <Grid container spacing={3}>
            {renderIntegrationSelect("idUnidade", "ID da unidade", seiIntegration.units, { required: true, md: 3 })}

            <Grid item size={{ xs: 12, md: 9 }}>
              <Autocomplete
                value={jsonConfig.documentos || []}
                onChange={handleChangeDocumentos}
                multiple
                id="sei-document-list"
                options={documentVariables}
                getOptionLabel={(fileField) => fileField}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    required
                    label={language.documentsToUpload || "Documentos para envio"}
                  />
                )}
              />
            </Grid>

            {renderField("tipo", "Tipo", { md: 3 })}
            {renderField("protocoloProcedimento", "Protocolo do procedimento", { md: 5 })}
            {renderField("idProcedimento", "ID do procedimento", { md: 4 })}
            {renderIntegrationSelect("idSerie", "ID série", seiIntegration.series, { md: 3 })}
            {renderField("numero", "Número", { md: 3 })}
            {renderField("data", "Data", { placeholder: "01/01/2000", md: 3 })}
            {renderField("idTipoConferencia", "ID tipo conferência", { md: 3 })}
            {renderField("descricao", "Descrição", { md: 6 })}
            {renderField("observacao", "Observação", { md: 6 })}
            {renderField("nivelAcesso", "Nível de acesso", { md: 4 })}
            {renderField("idHipoteseLegal", "ID hipótese legal", { md: 4 })}
            {renderField("sinBloqueado", "Bloqueado", { md: 4 })}
            {renderField("remetenteSigla", "Remetente sigla", { md: 3 })}
            {renderField("remetenteNome", "Remetente nome", { md: 9 })}
            {renderField("interessadosSigla", "Interessado sigla", { md: 3 })}
            {renderField("interessadosNome", "Interessado nome", { md: 9 })}
            {renderField("destinatariosSigla", "Destinatário sigla", { md: 3 })}
            {renderField("destinatariosNome", "Destinatário nome", { md: 9 })}
            {renderField("resultVariable", "Variável de retorno", { md: 6 })}
          </Grid>
        </FormPadding>

        {hasConfigError && (
          <GenericSnackbar
            errorWithChildren={true}
            message={
              <div>
                {hasConfigError.map((err, index) => (
                  <div key={`seiError_${index}`}>{err}</div>
                ))}
              </div>
            }
            handleClose={() => setHasConfigError(false)}
            duration={4000}
          />
        )}
      </GenericModal>
    )
  );
}

ModelerModalIntegrarSei.propTypes = {
  saveIntegrarSeiConfig: PropTypes.func,
  businessObject: PropTypes.object,
  element: PropTypes.object,
  extensionElements: PropTypes.object,
  bpmnFactory: PropTypes.object,
  FormComponents: PropTypes.array,
  inputValues: PropTypes.object,
};

export default ModelerModalIntegrarSei;
